import { unsubscribeFeedParam, unsubscribeTargetParam } from '@dap/common/consts';
import { newErrorMessage, newSuccessMessage, useAppDispatch } from '@dap/common/data-access';
import { useToggleNotificationMutation } from '@dap/social-feed/data-access';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

type UnsubscribeKind = 'target' | 'feed';

// Turns off notifications for the target/feed named by the `unsubscribeTarget`/`unsubscribeFeed`
// query param, so a link in a notification email can unsubscribe the user from whatever page it
// lands on. The params are removed again right away so a refresh doesn't repeat the request.
export function useUnsubscribeFromQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('notifications');
  const dispatch = useAppDispatch();
  const [toggleNotification] = useToggleNotificationMutation();
  // Requests in flight, so a re-run of the effect (e.g. StrictMode) can't send them twice.
  const inFlight = useRef(new Set<string>());

  const targetId = searchParams.get(unsubscribeTargetParam);
  const feedId = searchParams.get(unsubscribeFeedParam);

  useEffect(() => {
    if (!targetId && !feedId) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(unsubscribeTargetParam);
        next.delete(unsubscribeFeedParam);
        return next;
      },
      // Replace rather than push, so the back button doesn't return to the unsubscribe link.
      { replace: true }
    );

    const requests: { kind: UnsubscribeKind; id: string }[] = [];
    if (targetId) {
      requests.push({ kind: 'target', id: targetId });
    }
    if (feedId) {
      requests.push({ kind: 'feed', id: feedId });
    }

    requests.forEach(({ kind, id }) => {
      const key = `${kind}:${id}`;
      if (inFlight.current.has(key)) {
        return;
      }
      inFlight.current.add(key);

      toggleNotification({ targetId: id, notify: false })
        .unwrap()
        .then(() => dispatch(newSuccessMessage(t(`notifications:unsubscribe.${kind}.success`))))
        .catch(() => dispatch(newErrorMessage(t(`notifications:unsubscribe.${kind}.error`))))
        .finally(() => inFlight.current.delete(key));
    });
  }, [targetId, feedId, setSearchParams, toggleNotification, dispatch, t]);
}
